import { useState } from 'react';
import { toast } from 'sonner';
import api from '../utils/api';
import { Search, UserPlus, Clock, MapPin } from 'lucide-react';

/**
 * "Find Players to Invite" — searches players NOT already in the club
 * and lets club admins send them a club invite. Pending invites persist
 * across reloads via the backend `pending_invite` flag.
 */
export default function FindPlayersToInvite({ club }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inviting, setInviting] = useState(null);
  const [invitedSet, setInvitedSet] = useState(new Set());

  const searchPlayers = async () => {
    if (!club) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('name', search);
      const res = await api.get(`/clubs/${club.id}/discover-players?${params}`);
      setPlayers(res.data);
      const pending = res.data.filter(p => p.pending_invite).map(p => p.id);
      if (pending.length) setInvitedSet(prev => new Set([...prev, ...pending]));
    } catch {} finally { setLoading(false); }
  };

  const invitePlayer = async (playerId, playerName) => {
    setInviting(playerId);
    try {
      await api.post(`/clubs/${club.id}/invite-player/${playerId}`, { message: '' });
      toast.success(`Invite sent to ${playerName}`);
      setInvitedSet(prev => new Set([...prev, playerId]));
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setInviting(null); }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && players.length === 0) searchPlayers();
  };

  return (
    <>
      <button onClick={toggle}
        className="flex items-center gap-2 h-10 px-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-all"
        data-testid="discover-players-btn">
        <Search size={14} /> Find Players to Invite
      </button>

      {open && (
        <div className="bg-white rounded-xl border border-blue-200 shadow-sm overflow-hidden mt-3" data-testid="player-discovery">
          <div className="px-5 py-3 border-b border-blue-100 bg-blue-50 flex items-center gap-2">
            <Search size={14} className="text-blue-600" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') searchPlayers(); }}
              placeholder="Search by name, nickname..."
              className="flex-1 h-8 bg-white rounded-lg border border-blue-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="discover-search-input" />
            <button onClick={searchPlayers} className="h-8 px-3 bg-blue-600 text-white text-xs font-semibold rounded-lg">Search</button>
          </div>
          {loading ? (
            <div className="px-5 py-6 text-center text-sm text-slate-400">Searching...</div>
          ) : players.length === 0 ? (
            <div className="px-5 py-6 text-center text-sm text-slate-400">No players found</div>
          ) : (
            <div className="divide-y divide-slate-100 max-h-80 overflow-y-auto">
              {players.map(p => (
                <div key={p.id} className="px-5 py-2.5 flex items-center gap-3" data-testid={`discover-player-${p.id}`}>
                  <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-xs font-bold text-blue-700">
                    {p.name?.[0]?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900">{p.name} <span className="text-xs text-slate-400 font-mono">(Lifetime: {p.lifetime_doubles_rating || p.doubles_rating})</span></p>
                    <div className="flex items-center gap-2 text-[10px] text-slate-500">
                      {p.city && <span className="flex items-center gap-0.5"><MapPin size={8} />{p.city}</span>}
                      <span>{p.availability_status || 'unknown'}</span>
                    </div>
                  </div>
                  {invitedSet.has(p.id) ? (
                    <span className="h-7 px-3 bg-amber-50 text-amber-700 text-xs font-semibold rounded-lg flex items-center gap-1 border border-amber-200"
                      data-testid={`invited-player-${p.id}`}>
                      <Clock size={11} /> Pending Invite
                    </span>
                  ) : (
                    <button onClick={() => invitePlayer(p.id, p.name)} disabled={inviting === p.id}
                      className="h-7 px-3 bg-green-600 hover:bg-green-700 disabled:bg-slate-300 text-white text-xs font-semibold rounded-lg flex items-center gap-1"
                      data-testid={`invite-player-${p.id}`}>
                      <UserPlus size={11} /> Invite
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}
